import * as React from 'react';
import noop from 'lodash/noop';
import {emptyObject} from '@packages/utilities/fp/utils';

import {type Json} from '@packages/utilities/json';
import {
  CircleCheckLarge as CircleCheck,
  IcoPlay12 as PlayIcon,
} from '@packages/systems/ui/icons';
import {NotAllowedIcon} from '@packages/systems/spring';
import {ConfirmDialog, AlertDialog} from '@packages/systems/dialog';
import {Copy, Subheading} from '@packages/systems/design-system/Text';
import {
  styled,
  styledDiv,
  ThemeContext,
  designerTheme,
  emotionStyled,
  tokens,
} from '@packages/utilities/styled';
import TooltipTrigger from '@packages/systems/design-system/TooltipTrigger';
import Button from '@packages/systems/design-system/Button';

import {OutputPreview} from './OutputPreview';
import LoadingIndicatorCircle from '@packages/systems/design-system/LoadingIndicatorCircle';

import Grid from '@packages/systems/design-system/Grid';
import Flexbox from '@packages/systems/design-system/Flexbox';

import {FlowTestStates} from './types';

type Props = {
  /** The title of the modal. */
  title: string | React.ReactElement<any>;
  /**
   * Input data for the modal, which is rendered in the left column of the
   * modal. Pass in `null` if there is no input data to provide, and the
   * modal will only render the Output data column.
   */
  input: null | {
    /**
     * Optional title for the input column. Defaults to "Input data".
     * Pass in `null` to hide the title.
     */
    title?: null | string;
    /** The content body for the Input data section, like form field components etc. */
    content: React.ReactNode;
    /** Description or summary of the input data. */
    description: React.ReactNode;
  };
  /**
   * Out data for the modal, which is rendered in the right column of the
   * modal. This UI will render all the relevant information about the
   * result of their test.
   */
  output: {
    /**
     * Optional title for the output column. Defaults to "Output data".
     * Pass in `null` to hide the title.
     */
    title?: null | string;
    /** Description or summary of the output data. */
    description: React.ReactNode;
    /** Test run state. */
    testState: {
      /** Test run status. Defaults to 'idle'. */
      status: keyof typeof FlowTestStates;
      /** Test run error state. If error exists, we'll render the error Note. */
      error?: {
        /** Error title. */
        title: React.ReactNode;
        /** Error description. */
        description: React.ReactNode;
        /** Optional callback to retry the test, and renders the retry button. */
        onRetry?: () => void;
        /** Optional note rendered to the side, e.g. show the status code: `Status: 400`. */
        sideNote?: React.ReactNode;
      };
      /** Test run success state. If success exists, we'll render the success Note. */
      success?: {
        /** Success description */
        description: React.ReactNode;
        /** Optional note rendered to the side, e.g. show the status code: `Status: 400`. */
        sideNote?: React.ReactNode;
      };
      /** Test run idle state. If no test is running or has run, UI defaults to this gray box state. */
      idle?: {
        /** Idle description. Defaults to 'Test results will appear here...' */
        description: React.ReactNode;
      };
      /** JSON data to render in the UI. */
      json: undefined | Json;
    };
    /** The "Run test" button configuration. */
    runTestConfig?: {
      /** Should the button be blue (primary) or gray (default)? Defaults to 'primary'. */
      intent?: 'primary' | 'default';
      /** Button disabled state */
      disabled?: boolean;
      /** Button CTA label, defaults to "Run test". */
      label?: React.ReactNode;
      /** Optional icon for the button CTA label. */
      icon?: React.ReactNode;
      /** Tooltip to render on the button. */
      tip?: React.ReactNode;
      /** Callback that is invoked when the Run test button is clicked. */
      onRunTest: () => void;
    };
  };
  /**
   * Optional "Apply data" button configuration, typically used for
   * running a test to apply response data.
   */
  applyDataConfig?: {
    /** Should the button be blue (primary) or gray (default)? */
    intent?: 'primary' | 'default';
    /** Optional button label, defaults to "Apply data". */
    label?: React.ReactNode;
    /** Tooltip to render on the button. */
    tip?: React.ReactNode;
    /** Button disabled state. */
    disabled?: boolean;
    /** Callback that is invoked when the Apply data button is clicked. */
    onApplyData: () => void;
  };
  cancel?: {
    /** Optional button label, defaults to "Cancel". */
    label?: React.ReactNode;
    /** Optional callback that is invoked on cancel (or modal closed via the X button or outside click. */
    onClick?: () => void;
  };
};

/**
 * This is a generic UI component that powers the Run test and Apply work flow.
 *
 * NOTE NOTE NOTE =========================================
 *
 * Do not bake in any flux state, hooks, or logic that is specific to a block.
 * Try to extract what the behavior should be, and create the appropriate props
 * for it.
 *
 * NOTE NOTE NOTE =========================================
 */
export const FlowTestModal = ({
  title,
  input,
  output,
  applyDataConfig,
  cancel,
}: Props) => {
  const {label: cancelLabel = 'Cancel', onClick: onCancelClick = noop} =
    cancel || emptyObject;
  const {
    title: outputTitle = 'Output data',
    description: outputDescription,
    runTestConfig,
    testState: {status, error, success, idle, json},
  } = output;

  const hasInput = input != null;

  const dialogStyle = React.useMemo(
    () => ({
      width: hasInput ? DIALOG_LARGE : DIALOG_SMALL,
      minHeight: DIALOG_SMALL,
    }),
    [hasInput]
  );

  const dialogContent = (
    <>
      <Grid
        style={MIN_CONTENT_HEIGHT}
        gridAutoFlow="column"
        gridGap={0}
        gridTemplateColumns={
          input ? `1fr min-content ${DIALOG_SMALL}px` : '1fr'
        }
      >
        {/* Input column */}
        {input && (
          <>
            <div>
              {input.title === null ? null : (
                <StyledSubheading>
                  {input.title || 'Input data'}
                </StyledSubheading>
              )}

              <Copy>{input.description}</Copy>

              {input.content}
            </div>

            {/* Divider (middle) column */}
            <VerticalDivider />
          </>
        )}

        {/* Output column */}
        <div>
          {outputTitle === null ? null : (
            <StyledSubheading>{outputTitle}</StyledSubheading>
          )}

          <StyledDescription>{outputDescription}</StyledDescription>
          {runTestConfig && (
            <StyledTestButton
              primary={
                runTestConfig.intent === 'primary' ||
                runTestConfig.intent == null
              }
              disabled={
                runTestConfig.disabled ||
                // Disable the button if the test is in progress to prevent
                // users from clicking it again while it's running
                status === FlowTestStates.inProgress
              }
              onClick={runTestConfig.onRunTest}
              data-automation-id="flow-test-modal-run-test-button"
            >
              <LabelWithTooltip
                tip={runTestConfig.tip}
                label={
                  <>
                    {runTestConfig.icon === undefined ? (
                      <StyledIconWrapper>
                        <PlayIcon />
                      </StyledIconWrapper>
                    ) : (
                      runTestConfig.icon
                    )}

                    {runTestConfig.label || 'Run test'}
                  </>
                }
              />
            </StyledTestButton>
          )}

          <StatusBox
            json={json}
            status={status}
            description={
              idle?.description || 'Test results will appear here...'
            }
            success={success}
            error={error}
          />
        </div>
      </Grid>
      <HorizontalDivider />
    </>
  );

  // Confirm dialog case, because the user wants to submit/apply some data
  if (applyDataConfig) {
    return (
      <ThemeContext.Provider value={designerTheme}>
        <ConfirmDialog
          title={title}
          style={dialogStyle}
          content={dialogContent}
          submit={{
            'data-automation-id': 'flow-test-modal-apply-data-button',
            label: (
              <LabelWithTooltip
                tip={applyDataConfig.tip}
                label={applyDataConfig.label || 'Apply data'}
              />
            ),
            disabled: Boolean(applyDataConfig.disabled),
            intent: applyDataConfig.intent || 'primary',
          }}
          cancel={{label: cancelLabel, intent: 'default'}}
          onCancel={onCancelClick}
          onSubmit={applyDataConfig.onApplyData}
        />
      </ThemeContext.Provider>
    );
  }

  return (
    <ThemeContext.Provider value={designerTheme}>
      <AlertDialog
        title={title}
        style={dialogStyle}
        content={dialogContent}
        submit={{label: cancelLabel, intent: 'default'}}
        onSubmit={onCancelClick}
      />
    </ThemeContext.Provider>
  );
};

// @ts-expect-error - TS7031 - Binding element 'status' implicitly has an 'any' type. | TS7031 - Binding element 'json' implicitly has an 'any' type. | TS7031 - Binding element 'description' implicitly has an 'any' type. | TS7031 - Binding element 'success' implicitly has an 'any' type. | TS7031 - Binding element 'error' implicitly has an 'any' type.
const StatusBox = ({status, json, description, success, error}) => {
  if (status === FlowTestStates.idle) {
    return (
      <StatusContainer data-automation-id="status-container">
        {description}
      </StatusContainer>
    );
  }

  if (status === FlowTestStates.inProgress) {
    return (
      <StatusContainer data-automation-id="status-container">
        <LoadingIndicatorWrapper>
          {/* @ts-expect-error - TS2769 - No overload matches this call. */}
          <LoadingIndicatorCircle small />
        </LoadingIndicatorWrapper>
        <span>Running test...</span>
      </StatusContainer>
    );
  }

  if (status === FlowTestStates.success) {
    return (
      <>
        {success && (
          <StatusWrapper success data-automation-id="test-request-success">
            <CircleCheck />
            <span>{success.description}</span>
            {success.sideNote && (
              <SideNoteWrapper>{success.sideNote}</SideNoteWrapper>
            )}
          </StatusWrapper>
        )}

        {json && <OutputPreview outputJson={json} />}
      </>
    );
  }

  if (status === FlowTestStates.error && error) {
    return (
      <StatusWrapper error data-automation-id="test-request-error">
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <NotAllowedIcon size={18} />
        <StatusTitle>{error.title}</StatusTitle>
        {error.sideNote && <SideNoteWrapper>{error.sideNote}</SideNoteWrapper>}
        <div style={{gridColumn: '2 / 4', gridRow: 2}}>
          <span>{error.description}</span>
        </div>
        {error.onRetry && (
          <RetryButton sizing="large" onClick={error.onRetry}>
            Retry
          </RetryButton>
        )}
      </StatusWrapper>
    );
  }

  return null;
};

// @ts-expect-error - TS7031 - Binding element 'tip' implicitly has an 'any' type. | TS7031 - Binding element 'label' implicitly has an 'any' type.
const LabelWithTooltip = ({tip, label}) => (
  <TooltipTrigger
    content={tip ? <TipContentWrapper>{tip}</TipContentWrapper> : ''}
    alternate
  >
    <Flexbox alignItems="center" justifyContent="center">
      {label}
    </Flexbox>
  </TooltipTrigger>
);

// These are widths we want to manually specify
// for different variations of our dialog
// when there's bindings to be entered
const DIALOG_LARGE = 800;
const DIALOG_SMALL = 480;
// min-height of the modal content to force modal to be 480px in height
const MIN_CONTENT_HEIGHT = {minHeight: 346} as const;

const TIP_CONTENT_WIDTH = 224;

const TipContentWrapper = emotionStyled('div')({
  maxWidth: TIP_CONTENT_WIDTH,
});

const RetryButton = styled(Button)(({theme: {colors}}) => ({
  gridColumn: 2,
  background: colors.dangerBackground,
  color: colors.white,
  borderColor: 'currentColor',
}));

const StyledIconWrapper = emotionStyled('div')({
  paddingRight: tokens.space[1],
  paddingLeft: tokens.space[1],
});

const StyledSubheading = styled(Subheading)(({theme}) => ({
  marginBottom: theme.gutters.x16,
}));

const StyledTestButton = styled(Button)(({theme}) => ({
  width: '100%',
  height: theme.Button.size.large,
  fontSize: theme.typography.copy.fontSize,
  marginBottom: theme.gutters.x12,
}));

const StyledDescription = styled(Copy)(({theme}) => ({
  marginBottom: theme.gutters.x12,
  fontSize: theme.typography.copy.fontSize,
}));

const HorizontalDivider = styledDiv(({theme}) => ({
  background: theme.colors.border,
  height: 1,
  marginTop: theme.gutters.x12,
  marginLeft: -theme.gutters.x16,
  width: `calc(100% + ${2 * theme.gutters.x16}px)`,
}));

const VerticalDivider = styledDiv(({theme}) => ({
  background: theme.colors.border,
  width: 1,
  marginLeft: theme.gutters.x16,
  marginRight: theme.gutters.x16,
  gridColumn: 2,

  marginTop: -theme.gutters.x16,
  height: `calc(100% + ${2 * (theme.gutters.x12 + 2)}px)`,
}));

const StatusWrapper = styledDiv(
  ({theme}) => ({
    padding: theme.gutters.x20,
    display: 'grid',
    gridTemplateColumns: `repeat(2, minmax(0px, max-content)) 1fr`,
    columnGap: theme.gutters.x12,
    alignItems: 'center',
    borderRadius: theme.control.borderRadiusDot,
    color: theme.colors.white,
  }),
  {
    success: ({theme}) => ({
      backgroundColor: theme.colors.successBackground,
    }),
    error: ({theme}) => ({
      height: 'min-content',
      backgroundColor: theme.colors.dangerBackground,
      gridTemplateRows: `repeat(2, minmax(0px, max-content))`,
      rowGap: theme.gutters.x16,
    }),
    invalid: {
      gridTemplateColumns: '1fr',
      justifyContent: 'center',
      alignItems: 'center',
    },
  }
);

const STATUS_CONTAINER_HEIGHT = 72;

const StatusContainer = emotionStyled('div')({
  border: `1px solid ${tokens.colors.border4}`,
  marginTop: tokens.space[2],
  backgroundColor: tokens.colors.background2,
  padding: tokens.space[2],
  borderRadius: tokens.controls.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: tokens.colors.textSecondary,
  height: STATUS_CONTAINER_HEIGHT,
});

const StatusTitle = styledDiv(({theme: {fontWeights}}) => ({
  fontWeight: fontWeights.heading,
}));

const SideNoteWrapper = emotionStyled('div')({justifySelf: 'end'});

const LoadingIndicatorWrapper = emotionStyled('div')({
  marginRight: tokens.space[2],
  marginTop: tokens.space[1],
});
